// Service de gestion des médias (images, vidéos, fichiers)
import type { MediaConfig } from '../Core/types';
import { MediaCacheService, type MediaCacheContext } from './MediaCacheService';
import type { MediaCacheConfig } from '../Config/config';

export interface MediaServiceConfig {
    baseUrl: string;
    readToken: string;
    mediaConfig: MediaConfig;
    minQuality?: number;  // Qualité minimale (défaut: 1)
    maxQuality?: number;  // Qualité maximale (défaut: 95)
    cacheConfig?: MediaCacheConfig; // Configuration du cache
}

export interface ImageOptions {
    quality?: number; // 0-95% (qualité JPEG pour l'API)
    width?: number;
    height?: number;
    context?: MediaCacheContext; // Contexte pour le cache
}

class MediaServiceClass {
    private config: MediaServiceConfig | null = null;
    private urlCache = new Map<string, string>();

    /**
     * Initialise le service avec la configuration
     */
    async init(config: MediaServiceConfig) {
        this.config = config;
        this.urlCache.clear();
        
        // Initialiser le cache si configuré
        if (config.cacheConfig) {
            try {
                await MediaCacheService.init(config.cacheConfig);
            } catch (error) {
                console.warn('⚠️ Erreur initialisation cache:', error);
            }
        }
        
        console.log('✅ MediaService initialisé avec baseUrl:', config.baseUrl);
    }

    /**
     * Vérifie si le service est initialisé
     */
    private ensureInitialized(): boolean {
        if (!this.config) {
            console.warn('⚠️ MediaService non initialisé - utilisation impossible');
            return false;
        }
        return true;
    }

    /**
     * Détecte si une source est un fichier local ou un ID API
     */
    isLocalFile(source: string): boolean {
        return (
            source.startsWith('/') ||
            source.startsWith('./') ||
            source.startsWith('http://') ||
            source.startsWith('https://') ||
            source.startsWith('data:')
        );
    }

    /**
     * Construit l'URL pour une image avec support du cache
     */
    async getImageUrl(source: string, options: ImageOptions = {}): Promise<string> {
        // Si c'est un fichier local, retourner tel quel
        if (this.isLocalFile(source)) {
            return source;
        }

        if (!this.ensureInitialized()) {
            // Retourner source tel quel si non initialisé (sera géré comme une erreur)
            return source;
        }

        // Vérifier le cache en priorité si un contexte est fourni
        if (options.context && MediaCacheService.isEnabled()) {
            try {
                const cachedUrl = await MediaCacheService.getCachedMediaUrl(source, options.context);
                if (cachedUrl) {
                    return cachedUrl;
                }
            } catch (error) {
                console.warn('⚠️ Erreur vérification cache:', error);
            }
        }

        // Créer une clé de cache
        const cacheKey = `image-${source}-${JSON.stringify(options)}`;
        if (this.urlCache.has(cacheKey)) {
            return this.urlCache.get(cacheKey)!;
        }

        // Construire l'URL API (config est garanti non-null après ensureInitialized)
        const url = new URL(
            `/api/v1/upload/public/serve/image/${source}`,
            this.config!.baseUrl
        );

        // Ajouter la qualité JPEG (borné par min/max configurables)
        const minQuality = this.config!.minQuality ?? 1;
        const maxQuality = this.config!.maxQuality ?? 95;
        const qualityPercent = options.quality !== undefined ? options.quality : 85;
        const boundedQuality = Math.min(maxQuality, Math.max(minQuality, qualityPercent));
        url.searchParams.set('quality', boundedQuality.toString());

        // Ajouter les dimensions si spécifiées
        if (options.width) {
            url.searchParams.set('width', options.width.toString());
        }

        if (options.height) {
            url.searchParams.set('height', options.height.toString());
        }

        // Ajouter le token de lecture
        url.searchParams.set('read_token', this.config!.readToken);

        const finalUrl = url.toString();
        this.urlCache.set(cacheKey, finalUrl);
        return finalUrl;
    }

    /**
     * Construit l'URL pour une vidéo
     */
    getVideoUrl(source: string): string {
        // Si c'est un fichier local, retourner tel quel
        if (this.isLocalFile(source)) {
            return source;
        }

        if (!this.ensureInitialized()) {
            return source;
        }

        const cacheKey = `video-${source}`;
        if (this.urlCache.has(cacheKey)) {
            return this.urlCache.get(cacheKey)!;
        }

        const url = new URL(
            `/api/v1/upload/public/serve/video/${source}`,
            this.config!.baseUrl
        );
        url.searchParams.set('read_token', this.config!.readToken);

        const finalUrl = url.toString();
        this.urlCache.set(cacheKey, finalUrl);
        return finalUrl;
    }

    /**
     * Construit l'URL pour le manifest HLS
     */
    getHLSUrl(source: string): string {
        // Si c'est un fichier local, retourner tel quel
        if (this.isLocalFile(source)) {
            return source;
        }

        if (!this.ensureInitialized()) {
            return source;
        }

        const cacheKey = `hls-${source}`;
        if (this.urlCache.has(cacheKey)) {
            return this.urlCache.get(cacheKey)!;
        }

        const url = new URL(
            `/api/v1/upload/public/hls/${source}/master.m3u8`,
            this.config!.baseUrl
        );
        url.searchParams.set('read_token', this.config!.readToken);

        const finalUrl = url.toString();
        this.urlCache.set(cacheKey, finalUrl);
        return finalUrl;
    }

    /**
     * Récupère le token de lecture
     */
    getReadToken(): string | null {
        if (!this.config) {
            return null;
        }
        return this.config.readToken;
    }

    /**
     * Récupère l'URL de base
     */
    getBaseUrl(): string | null {
        if (!this.config) {
            return null;
        }
        return this.config.baseUrl;
    }

    /**
     * Récupère la qualité minimale configurée
     */
    getMinQuality(): number {
        return this.config?.minQuality ?? 1;
    }

    /**
     * Récupère la qualité maximale configurée
     */
    getMaxQuality(): number {
        return this.config?.maxQuality ?? 95;
    }

    /**
     * Construit l'URL pour un fichier générique
     */
    getFileUrl(source: string): string {
        // Si c'est un fichier local, retourner tel quel
        if (this.isLocalFile(source)) {
            return source;
        }

        if (!this.ensureInitialized()) {
            return source;
        }

        const cacheKey = `file-${source}`;
        if (this.urlCache.has(cacheKey)) {
            return this.urlCache.get(cacheKey)!;
        }

        const url = new URL(
            `/api/v1/upload/public/serve/file/${source}`,
            this.config!.baseUrl
        );
        url.searchParams.set('read_token', this.config!.readToken);

        const finalUrl = url.toString();
        this.urlCache.set(cacheKey, finalUrl);
        return finalUrl;
    }

    /**
     * Teste si une URL est accessible (avec timeout)
     */
    async testUrl(url: string, timeout: number = 5000): Promise<boolean> {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(url, {
                method: 'HEAD',
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    /**
     * Nettoie le cache
     */
    clearCache() {
        this.urlCache.clear();
    }

    /**
     * Ferme le service et nettoie les ressources
     */
    close() {
        this.urlCache.clear();
        MediaCacheService.close();
        console.log('🔒 MediaService fermé');
    }
}

// Export singleton
export const MediaService = new MediaServiceClass();

